import { hash } from 'immutable';
import { handleActions } from 'redux-actions';

import type { QcsSyncResourceStore } from 'app-components/flux/core/resourceStoreTypes';

import { hasAsyncDispatch } from '../../utils/redux-store/asyncDispatchMiddleware';
import { resourceStoreTypes, resourceUndoUpserters, resourceUpserters } from '../core';
import { coreActions } from '../core-actions';
import * as actions from './datasetsActions';
import type { DatasetProps } from './datasetsTypes';

const QcsSyncDatasetsStoreFactory = resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<DatasetProps>();

type UndoAbleActions =
  | ReturnType<typeof actions.createDataset>
  | ReturnType<typeof actions.updateDataset>
  | ReturnType<typeof actions.deleteDataset>;

type SingleSuccessActions =
  | ReturnType<typeof actions.createDatasetSuccess>
  | ReturnType<typeof actions.updateDatasetSuccess>
  | ReturnType<typeof actions.deleteDatasetSuccess>;

type ResettableActions =
  | ReturnType<typeof actions.createDatasetFailure>
  | ReturnType<typeof actions.updateDatasetFailure>
  | ReturnType<typeof actions.deleteDatasetFailure>;

const undoUpsert = (state: QcsSyncResourceStore<DatasetProps>, action: UndoAbleActions) =>
  resourceUndoUpserters.upsertResourceSetupUndo(state, action);

const singleUpsert = (state: QcsSyncResourceStore<DatasetProps>, action: SingleSuccessActions) =>
  resourceUpserters.upsertResource(state, action.payload);

const reset = (state: QcsSyncResourceStore<DatasetProps>, action: ResettableActions) => {
  if (hasAsyncDispatch(action)) action.asyncDispatch(actions.getDatasets());
  return resourceUpserters.resetResources(state, action.payload);
};

const reducer = handleActions<
  resourceStoreTypes.QcsSyncResourceStore<DatasetProps>,
  | coreActions.SanitizeAction
  | ReturnType<typeof actions.getDatasets>
  | ReturnType<typeof actions.getDatasetsSuccess>
  | ReturnType<typeof actions.getDatasetsFailure>
  | UndoAbleActions
  | SingleSuccessActions
  | ResettableActions
  | ReturnType<typeof actions.getDatasetEntitlementsSuccess>
  | ReturnType<typeof actions.getDatasetEntitlementsFailure>
>(
  {
    [coreActions.SANITIZE]: (state) =>
      state.set(
        'resourcesById',
        state.resourcesById.map((resource) =>
          resource.merge({
            name: hash(resource.name).toString(),
          }),
        ),
      ),

    [actions.getDatasets.type]: (state) => state.merge({ isLoading: true }),
    [actions.getDatasetsSuccess.type]: (state, { payload }) => resourceUpserters.upsertResources(state, payload),
    [actions.getDatasetsFailure.type]: (state, { payload: error }) => resourceUpserters.completeWithError(state, error),

    [actions.getDatasetEntitlementsSuccess.type]: (state, { payload }) =>
      resourceUpserters.upsertResources(state, payload),
    [actions.getDatasetEntitlementsFailure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError(state, error),

    // POST actions
    [actions.createDataset.type]: undoUpsert,
    [actions.updateDataset.type]: undoUpsert,
    [actions.deleteDataset.type]: undoUpsert,

    // POST Success actions
    [actions.createDatasetSuccess.type]: singleUpsert,
    [actions.updateDatasetSuccess.type]: singleUpsert,
    [actions.deleteDatasetSuccess.type]: singleUpsert,

    // POST Failure actions (require reset)
    [actions.createDatasetFailure.type]: reset,
    [actions.updateDatasetFailure.type]: reset,
    [actions.deleteDatasetFailure.type]: reset,
  },
  QcsSyncDatasetsStoreFactory({ resourceName: 'DATASETS' }),
);
export default reducer;
